/**
 * TypeScript mirror of the HTTP contract documented in `docs/API.md` (v2).
 *
 * This file is the only place the wire format is described; nothing else in
 * the frontend re-declares a payload shape. Changing the contract means
 * changing `docs/API.md` first, then this file.
 */

// ------------------------------------------------------------------- errors

/** Body of every non-2xx response. */
export interface ApiErrorBody {
  error: string;
  message: string;
  details?: Record<string, unknown> | null;
}

// --------------------------------------------------------------------- auth

/**
 * How the server identifies people.
 *
 * `google` is Google Workspace SSO, run entirely server-side: the browser
 * follows one link and comes back holding an HttpOnly cookie it cannot read.
 * `dev` is the fenced laptop stub (`docs/AUTH.md` §1) and says so on screen.
 */
export type AuthMode = "google" | "dev";

/** `GET /api/v1/auth/config` — served without a session. */
export interface AuthConfig {
  mode: AuthMode;
  /** Where the browser goes to sign in; `null` in dev mode. */
  sign_in_url: string | null;
  /** The only e-mail domain the server accepts; `null` in dev mode. */
  domain: string | null;
  /**
   * Who to ask for research access — the server's `SUPPORT_CONTACT`, or the
   * first `ADMIN_EMAILS` entry.
   *
   * It comes from the server rather than from this bundle because it is the
   * one line a brand-new researcher reads, and a wrong one would otherwise
   * need the web image rebuilt and re-pushed to fix. `null` when the
   * deployment names nobody: the screen says so instead of inventing a
   * destination.
   */
  access_contact: string | null;
}

/**
 * Where an account stands in the approval flow (`docs/AUTH.md` §2).
 *
 * Signing in is not access: a first sign-in creates a `pending` account that
 * can do nothing until an admin approves it.
 */
export type AccountState = "pending" | "active" | "disabled";

// ------------------------------------------------------------------ waiting

/**
 * The machine whose arrival serves a waiting request's turn.
 *
 * It is not always a machine asked for by that request: a request held behind
 * an older one carries the machine being started for the older one, because
 * that is the machine which ends its wait. `for` says which of the two it is.
 */
export interface WaitingMachine {
  /** When the console asked for it. */
  requested_at: string;
  /**
   * `requested_at` plus the configured boot estimate.
   *
   * An estimate from the server's configuration, never a measurement of this
   * machine — everything showing it says "usually about" — and `null` where no
   * estimate is configured, in which case no screen invents a duration.
   */
  eta_at: string | null;
  /** Whose turn its arrival serves. */
  for: "you" | "the request ahead";
  /**
   * The server's verdict that the machine is overdue. **Never recomputed
   * here**: a laptop with a skewed clock would disagree with the trace and
   * with the capacity screen about the same machine.
   */
  late: boolean;
}

/** One request served before yours: who asked, how big, and when. */
export interface WaitingAhead {
  user: string;
  /** `null` on the CPU lane, where the request is counted in cores. */
  gpus: number | null;
  /** `null` on the GPU lane. */
  cores: number | null;
  /** When it was asked for — which is also its place in the line. */
  asked_at: string;
}

/**
 * The whole of a wait, in one object: `null` unless `state` is
 * `waiting_for_node` (`docs/API.md`, *Waiting, explained*).
 *
 * The server is the only author of the two sentences it carries, because it is
 * the only party that knows the lane, the machine being started for its head
 * and the reason no machine could be started. **`message` and `note` are
 * rendered verbatim** — never composed, re-capitalised, shortened or extended.
 */
export interface Waiting {
  /** 1-based place in the queue that really serves this request. */
  position: number;
  /**
   * When the request was made — **not** how long this wait has lasted.
   *
   * A resize puts a workspace back in the queue without changing when it was
   * asked for, so every screen says "asked 3 min ago" and never "waiting
   * 3 min". Nothing in the payload records when a particular wait began, and
   * nothing here invents it.
   */
  asked_at: string;
  /**
   * The most GPUs (cores) this request could take right now **on a single
   * machine** — the number admission itself decides on, because a workspace
   * runs on one machine and never spans two.
   *
   * It counts work that is winding down, so for about half a minute after a
   * workspace stops it can exceed the capacity screen's fleet-wide `gpu_free`.
   * That is why every rendering of it says *on one machine*. `null`, never
   * `0`, when the fleet could not be read: `0` would mean "the fleet is full",
   * which would be a guess.
   */
  free_now: number | null;
  /** The requests served first, in service order; `[]` at the head. */
  ahead: WaitingAhead[];
  /** `null` when no machine whose arrival serves this turn is on its way. */
  machine: WaitingMachine | null;
  /** The short clause a card or a queue row shows: lowercase, no full stop. */
  note: string;
  /** The headline sentence, true at the instant of the read. */
  message: string;
}

/**
 * One row of `capacity.queue` — who is waiting, in the order they are served.
 *
 * The entries link nowhere: only the owner and an admin may open the object
 * behind one, and a row that 404s for most readers is worse than a row that is
 * text. `id` is carried so a list has a stable key.
 */
export interface QueueEntry {
  position: number;
  kind: "workspace" | "job" | "cpu_workspace" | "cpu_run";
  id: string;
  user: string;
  /** `null` on the CPU lane. */
  gpus: number | null;
  /** `null` on the GPU lane. */
  cores: number | null;
  asked_at: string;
  /** The console's own clause about this request, rendered as it stands. */
  note: string;
}

// ----------------------------------------------------------------- capacity

/**
 * State of a single physical GPU.
 *
 * - `busy`: held by someone **and** a sample attributed to that device says it
 *   is computing.
 * - `held`: held, and whether it computes is not known — no telemetry, or
 *   telemetry that could not be attributed to a container. Also what a GPU
 *   taken by something outside the console looks like.
 * - `idle`: held, and measured doing nothing for a confirmed streak.
 * - `free`: allocatable right now.
 * - `unschedulable`: the machine is not accepting work (cordoned / not ready).
 */
export type GpuState = "busy" | "held" | "idle" | "free" | "unschedulable";

/** `ready` = usable now, `provisioning` = Scaleway is creating the machine. */
export type NodeStatus = "ready" | "provisioning" | "not_ready";

/** Who holds a GPU. */
export interface GpuHolder {
  kind: "workspace" | "job";
  id: string;
  name: string;
  user: string;
  /** When they took it; `null` when the start time is not known yet. */
  since: string | null;
}

export interface GpuSlot {
  index: number;
  state: GpuState;
  /** `null` when telemetry is unavailable — never guessed. */
  utilization_pct: number | null;
  memory_used_mb: number | null;
  memory_total_mb: number | null;
  holder: GpuHolder | null;
}

export interface CapacityNode {
  /** `null` while Scaleway is still creating it: it has no name yet. */
  name: string | null;
  /**
   * The short name a researcher says out loud — a world of Asimov's
   * Foundation, after Hari Seldon and the science of predicting what comes
   * next. Stable for the machine's life, unique across the fleet, and `null`
   * exactly when `name` is.
   */
  display_name: string | null;
  status: NodeStatus;
  instance_type: string | null;
  gpu_total: number;
  gpu_free: number;
  /**
   * Whether the console knows which physical device each holder got.
   *
   * False when the DCGM exporter publishes no pod labels: the counts stay
   * exact, but a square is a slot, not a device — so no percentage is shown
   * against it and its number is not a `nvidia-smi` index.
   */
  gpus_measured: boolean;
  /** `null` until the machine has actually joined. */
  created_at: string | null;
  /** Empty while the machine is still being created. */
  gpus: GpuSlot[];
}

/**
 * One machine pool: a shape of machine the console can start on demand.
 *
 * There are several — 4-GPU and 8-GPU machines — because the point of the
 * product is matching the machine to the need: small pools for small asks,
 * whole 8-GPU machines when somebody needs one.
 */
export interface PoolInfo {
  pool_id: string;
  /** The shape: how many GPUs one machine of this pool holds. */
  gpus_per_node: number;
  /** Pool name from Scaleway; `null` when the pool could not be read. */
  name: string | null;
  node_type: string | null;
  /**
   * The pool's GPU family (ADR-0020) — `h100`, `l40s` — derived on the
   * server from the node type. `null` exactly when the node type is.
   */
  family: string | null;
  /** Machines asked for — the ones still booting included (Scaleway's `size`). */
  current_nodes: number;
  /** Machines that can run a workspace right now. This is "running". */
  ready_nodes: number;
  max_nodes: number;
  /**
   * Machines of this pool deliberately kept running even when nobody uses
   * them, so the next request starts in seconds instead of a machine boot.
   * Configured on the server, never inferred; 0 means an idle pool goes back
   * to nothing.
   */
  floor: number;
  /**
   * When the floor applies, in words — "weekdays 08:00-21:00 (Europe/Paris)"
   * — or null when it applies around the clock. Outside those hours the pool
   * drains to zero like one with no floor, and a request starts a machine.
   */
  floor_hours: string | null;
  /** Whether the floor is being held right now; always true without hours. */
  floor_active: boolean;
  provisioning_nodes: number;
  /**
   * Machines that are neither ready nor coming and that nothing is doing
   * about: stuck starting, locked, wedged. A machine the console is giving
   * back is not one of these.
   */
  unavailable_nodes: number;
  /**
   * Machines Scaleway is taking back because the console lowered the floor.
   * Never counted in `unavailable_nodes`: an idle machine going home is the
   * product working, and calling it "stuck" is how a routine shrink reads as
   * an outage.
   */
  draining_nodes: number;
  autoscaling: boolean;
  /**
   * Why this pool is unknown, when it is. The GPU counts of the capacity
   * screen stay exact either way — they come from the cluster, not Scaleway.
   */
  error: string | null;
}

export interface Telemetry {
  ok: boolean;
  /** Why utilization is missing, when `ok` is false. */
  reason: string | null;
}

/**
 * What one GPU comes with — declared by the deployment, never measured.
 *
 * Multiply by the GPU count for a request; a workspace that already exists
 * publishes its own `cores` and `memory_gb`, and those are what a screen shows
 * for it.
 */
export interface PerGpu {
  cores: number;
  memory_gb: number;
  scratch_gb: number;
}

/** What the "new workspace" form offers, published by the server's settings. */
export interface CapacityDefaults {
  workspace_hours: number;
  max_workspace_hours: number;
  allowed_gpu_counts: number[];
  /**
   * The deployment's default GPU family (ADR-0020) — what a request that
   * names none means. `null` while no pool has been read.
   */
  gpu_family: string | null;
  /** How long GPUs may do nothing before the console starts a countdown. */
  idle_threshold_minutes: number;
  /** How long that countdown then runs before the workspace is stopped. */
  idle_grace_minutes: number;
  /**
   * The cores, memory and `/scratch` one GPU comes with.
   *
   * **Optional on purpose.** `web` and `api` ship as separate images at
   * separate revisions here, so a bundle can be newer than the server it talks
   * to; in that window `defaults` arrives without this object. A required type
   * would promise a number that is `undefined` and print "Each GPU comes with
   * undefined cores" — the invented figure this field exists to remove, in its
   * worst form. Every read of it is guarded, and a missing figure becomes
   * silence rather than a number.
   */
  per_gpu?: PerGpu;
}

/**
 * One GPU family the deployment holds (ADR-0020) — the per-family view the
 * form decides with. The lane-wide figures on `Capacity` aggregate across
 * families; these do not.
 */
export interface GpuFamily {
  family: string;
  gpu_total: number;
  gpu_used: number;
  gpu_free: number;
  /** GPU counts a request for THIS family would satisfy immediately. */
  can_start_now: number[];
  /** The counts a form may offer for this family — its biggest machine caps them. */
  allowed_gpu_counts: number[];
}

/** `GET /api/v1/capacity` — every number here is measured, never invented. */
export interface Capacity {
  gpu_total: number;
  gpu_used: number;
  gpu_free: number;
  /** GPU counts that would start immediately. Empty ⇒ a machine must boot. */
  can_start_now: number[];
  /**
   * How long a new machine usually takes, in minutes.
   *
   * An estimate from the server's configuration, not a measurement of this
   * pool — everything that shows it says "usually about". `null` when no
   * machine can be started at all (pool unknown, or at its ceiling).
   */
  node_start_eta_minutes: number | null;
  /**
   * One entry per GPU family the deployment holds (ADR-0020). A pool that
   * could not be read contributes no family rather than a guessed one, so
   * this can name fewer families than exist — never more.
   */
  families: GpuFamily[];
  /**
   * The machine pools, one per shape in `SCW_POOLS`, smallest first.
   *
   * A pool Scaleway could not describe keeps its entry, with its own `error`
   * set and the API-derived fields null or zero — never invented.
   */
  pools: PoolInfo[];
  /**
   * Set only when no pool is configured at all (`pools` is then empty), in
   * one sentence a researcher can act on. The GPU counts above stay exact
   * either way — they come from the cluster, not from Scaleway.
   */
  pools_error: string | null;
  telemetry: Telemetry;
  nodes: CapacityNode[];
  defaults: CapacityDefaults;
  /**
   * Who is waiting for a GPU machine, in the order they will be served.
   *
   * `[]` when nobody is waiting — never `null`: an empty queue is a
   * measurement, not an unknown, and it is published even on a pass that could
   * not read the machines, because who is in line is a fact of the console's
   * own records. The CPU lane has its own list in `cpu.queue`, and the two are
   * never merged.
   */
  queue: QueueEntry[];
  /**
   * The CPU fleet — a **sibling** of the GPU fields, never mixed into them.
   *
   * `null` means the deployment has no CPU pools configured at all, which is a
   * different answer from "no cores are free": there is no CPU section to
   * show. The two fleets are never unioned — a CPU machine must never appear
   * in `nodes[]` and a GPU machine must never appear in `cpu.nodes[]`.
   */
  cpu: CpuCapacity | null;
}

// ------------------------------------------------------------- CPU capacity

/**
 * One CPU machine pool.
 *
 * It carries `zone`, which the GPU pools do not need to: a `/home` block
 * volume attaches inside one zone only, so a CPU pool outside the console's
 * zone could never mount anybody's files. Such a pool keeps its entry with
 * `error` set and is never grown.
 */
export interface CpuPoolInfo {
  pool_id: string;
  name: string | null;
  node_type: string | null;
  /** `null` when the pool could not be read, like every API-derived field. */
  zone: string | null;
  /**
   * Cores one machine of this pool advertises.
   *
   * Configuration, and therefore an estimate: what a machine really offers is
   * measured on the machine and published per machine, always lower.
   */
  cores_per_node: number;
  current_nodes: number;
  ready_nodes: number;
  /**
   * `MAX_CPU_MACHINES` — the **fleet-wide** ceiling, in the key the GPU pools
   * use for their per-pool one. It must never be summed across CPU pools.
   */
  max_nodes: number;
  /** Machines deliberately kept running; a floor is money, and it is visible. */
  floor: number;
  /** When the floor applies, in words; null when around the clock. */
  floor_hours: string | null;
  /** Whether the floor is being held right now; always true without hours. */
  floor_active: boolean;
  provisioning_nodes: number;
  /** Neither ready nor coming, and nothing is doing about it. */
  unavailable_nodes: number;
  /** Being taken back because the console lowered the floor. Not a fault. */
  draining_nodes: number;
  /**
   * What one machine of this shape costs an hour, as the deployment declared
   * it in `MACHINE_PRICE_EUR_HOUR`.
   *
   * Declared, never measured — the same class of fact as `cores_per_node` —
   * and `null` for a shape nobody priced. No price is ever averaged across
   * shapes or guessed from a core count.
   */
  price_per_machine_hour_eur: number | null;
  autoscaling: boolean;
  /** Why this pool is unusable — a wrong zone reads here, naming both zones. */
  error: string | null;
}

/**
 * One CPU machine, in whole cores.
 *
 * Every count is nullable because they all vanish together: when the console
 * could not make the cluster-wide read, it publishes no number rather than a
 * wrong one.
 */
export interface CpuNode {
  /** `null` while the machine is still being created: it has no name yet. */
  name: string | null;
  status: NodeStatus;
  instance_type: string | null;
  /** What the machine really allocates — never the brochure figure. */
  cores_total: number | null;
  /**
   * Cores handed to tasks. Reservable, not measured: nothing here says how
   * hard a core is working, and the interface must never imply that it does.
   */
  cores_used: number | null;
  cores_free: number | null;
  /** What the cluster's own daemons already asked for, published so the sum checks out. */
  system_cores: number | null;
  memory_total_gb: number | null;
  memory_free_gb: number | null;
  created_at: string | null;
  /** How many tasks of console runs are on this machine — a count, not a name. */
  tasks: number | null;
  /**
   * How many interactive CPU workspaces are on this machine.
   *
   * A separate number from `tasks` because the two end differently — a task
   * when its command exits, a workspace at its deadline — and somebody looking
   * at a machine that will not drain wants to know which of the two holds it.
   */
  workspaces: number | null;
}

/** What the two CPU forms may offer, published by the server. */
export interface CpuDefaults {
  run_hours: number;
  max_run_hours: number;
  max_tasks_per_run: number;
  /**
   * `MAX_TASK_RETRIES`: the most a run may ask for per task.
   *
   * What a run **gets** is still 0 — a command that may have had side effects
   * is never silently re-run — so this is only what a researcher may opt into.
   */
  max_task_retries: number;
  /**
   * The size of the shared folder a run of more than one task gets.
   *
   * `null` on a console that has no shared run storage, which is exactly the
   * console where `tasks > 1` is refused: a client that reads it knows which
   * of the two consoles it is talking to before it offers anybody a fan-out.
   */
  work_gb: number | null;
  /**
   * The core counts a CPU workspace may be asked for.
   *
   * `ALLOWED_CPU_WORKSPACE_CORES` filtered to what one whole machine of the
   * biggest CPU shape the console can start could hold **and** feed. It is the
   * sibling of `defaults.allowed_gpu_counts` and says what may be *asked for*;
   * `largest_free_machine_cores` says what starts now, and asking for more
   * than that is a wait for a machine, not a refusal. It is the same list
   * `400 invalid_core_count` validates against, so the form can never offer a
   * size the console would then turn down.
   */
  allowed_workspace_cores: number[];
}

/**
 * The CPU half of `GET /api/v1/capacity`.
 *
 * `measured` and `reason` are the honesty pair. Free cores are what the
 * scheduler would grant, which is only knowable from a cluster-wide read; when
 * that read is refused, **every count here is `null`** and the interface shows
 * no number at all. Half a truth about cores is worse than no answer: it would
 * report a full machine as empty.
 */
export interface CpuCapacity {
  measured: boolean;
  /** What is missing, when `measured` is false. */
  reason: string | null;
  cores_total: number | null;
  cores_used: number | null;
  cores_free: number | null;
  system_cores: number | null;
  /**
   * The most cores **one task** may ask for, and the number
   * `400 invalid_core_count` names.
   *
   * Bounded by memory as well as by cores, so it is never `cores_per_node` and
   * never the sum across machines: one task never spans machines, however many
   * machines the run itself uses.
   */
  largest_free_machine_cores: number | null;
  /** CPU machines the cluster can see right now. */
  machines_running: number | null;
  /**
   * CPU machines Scaleway has been asked for, booting ones included.
   *
   * This is the number every ceiling decision is made against, so it is the
   * one a screen puts beside `machines_max` — `node_limit_reached` counted it,
   * and *"64 are already running"* beside a fleet list of 40 is a
   * contradiction nobody can resolve. **No order between the two is promised**:
   * a machine being deleted leaves the pool before it leaves the cluster.
   */
  machines_requested: number | null;
  /** `MAX_CPU_MACHINES`: a fleet bug-guard, never a person's ration. */
  machines_max: number | null;
  /**
   * Why the console is not giving machines back right now, in the server's
   * own sentence — and `null` while the fleet drains normally, which is nearly
   * always.
   *
   * **Not** the `measured`/`reason` pair: that one says every count here is
   * `null`, this one is published beside counts that are perfectly good, to
   * say that the fleet is held at its current size and billing there because
   * the last pass could not read it. A screen that merged them would report
   * missing numbers at the moment the thing worth reporting is spend.
   */
  shrink_paused_reason: string | null;
  /**
   * What the CPU machines cost per hour right now, at the declared rates.
   *
   * `machines_requested` × the price of their shape — counted from the
   * machines *asked for*, because a machine bills from the moment it is
   * created. `null` whenever any pool could not be read or any running shape
   * has no configured price, with `fleet_per_hour_reason` saying which: a
   * figure that is wrong about part of the fleet is not published. It is a
   * **rate for the fleet**, never anybody's share of anything.
   */
  fleet_per_hour_eur: number | null;
  /** Why the fleet's rate is missing, when it is. */
  fleet_per_hour_reason: string | null;
  /**
   * The most the CPU fleet can cost in an hour: its own bound times the
   * dearest configured CPU shape.
   *
   * Not a forecast that it will, and `null` with its own reason under the same
   * rule as above — raising a ceiling should be a decision somebody makes with
   * this number in front of them.
   */
  ceiling_per_hour_eur: number | null;
  /** Why the ceiling's rate is missing, when it is. */
  ceiling_per_hour_reason: string | null;
  /**
   * How long a CPU machine takes to start.
   *
   * `null` until somebody measures one — the GPU figure is not reused, most of
   * it was the GPU driver install. The screen then says a machine has to start
   * without inventing how long.
   */
  node_start_eta_minutes: number | null;
  pools: CpuPoolInfo[];
  defaults: CpuDefaults;
  /**
   * Who is waiting for cores, in the order they will be served: CPU
   * workspaces before runs, oldest first inside each.
   *
   * The one thing here that survives `measured: false` — it is read from the
   * console's own records rather than from the machines, so it needs no
   * cluster read to be true. Publishing `[]` on a blind pass would say "nobody
   * is waiting" at the exact moment the console cannot see, while it knows
   * precisely who is in line. A waiting run appears here, with the clause the
   * console last wrote in its trace, even though a run carries no `waiting`
   * object of its own.
   */
  queue: QueueEntry[];
  /**
   * At most 24 machines, in a contracted order: the ones holding work first,
   * then the ones holding nothing, then the ones still being created.
   *
   * One object per machine on a five-second poll is the part of this payload
   * that grows with the fleet, so the list is cut. What a screen loses is the
   * empty end of the fleet, which is the part nobody is looking for.
   */
  nodes: CpuNode[];
  /**
   * How many machines `nodes` left out, `0` when none were.
   *
   * Shown, never dropped quietly: a screen listing 24 machines of 91 with no
   * indication is invented data. The counts above are always the **whole**
   * fleet's, listed or not, so nothing has to be reconciled against this list.
   */
  nodes_omitted: number;
}

// ----------------------------------------------------------------- CPU runs

/**
 * A run walks the job states.
 *
 * `expiring` is accepted because `docs/API.md` says a run enters it when its
 * warning time passes, exactly as a workspace does; the enumeration it gives
 * beside that sentence is the job walk. Accepting the value is not the same as
 * relying on it — the deadline warning on screen is derived from `warning_at`,
 * which is measured data either way.
 */
export type CpuRunState = JobState | "expiring";

/** Run states after which nothing moves any more. */
export const TERMINAL_CPU_RUN_STATES: readonly CpuRunState[] = [
  "succeeded",
  "failed",
  "cancelled",
  "expired",
];

/**
 * One line of a run's trace, with the index that makes it addressable.
 *
 * The index counts from 0 in the order the console wrote the events, and
 * `GET /cpu-runs/{id}?events_after=<index>` returns only what came after it. A
 * run of a thousand tasks appends a `progress` line on every pass in which the
 * finished count moved, and a page that re-read a hundred sentences every ten
 * seconds to find the one new one would be paying for the trace instead of
 * reading it.
 */
export interface CpuRunEvent extends WorkloadEvent {
  index: number;
}

/**
 * A CPU run: a command, a size in cores, a deadline, and the submitter's
 * `/home`.
 *
 * It is a third workload kind beside the workspace and the job, not a
 * workspace with no GPU. There is no connection block, no resize and — by
 * decision, not by omission — no `idle_since` and no `idle_stops_at`: nothing
 * on a CPU machine can tell a computing process from a waiting one.
 */
export interface CpuRun {
  id: string;
  user: string;
  name: string;
  state: CpuRunState;
  /** Cores **one task** holds — never the run's total, at any task count. */
  cores: number;
  hours: number;
  image: string;
  /** Stripped for anybody but the owner and an admin, exactly as a job's is. */
  command: string;
  /** The small integer scripts read as `$SLURM_JOB_ID`, and people say aloud. */
  run_number: number;
  tasks: number;
  tasks_waiting: number;
  tasks_running: number;
  tasks_succeeded: number;
  tasks_failed: number;
  /**
   * Tasks that never received a container before the run ended.
   *
   * The fifth counter, and it exists so that "failed" keeps meaning *your code
   * failed*: closing these as failures would hand somebody a list of failures
   * to debug that never ran a line of their code. The five counters always sum
   * to `tasks`, at every moment of the run's life.
   */
  tasks_not_run: number;
  retries: number;
  max_parallel_tasks: number | null;
  /** Machines held right now, measured from the cluster. 0 while waiting. */
  machines: number;
  cores_running: number;
  created_at: string;
  started_at: string | null;
  /** `null` while waiting for a machine: the clock starts when work does. */
  expires_at: string | null;
  warning_at: string | null;
  finished_at: string | null;
  queue_position: number | null;
  /**
   * When a machine was really requested **for this run**.
   *
   * `null` means none was asked for by this run — not that none is coming: a
   * machine being started for the request in front of it serves its turn too.
   * A run carries no `waiting` object (its turn also depends on tasks still to
   * place for runs already going), so its trace is where that is said.
   */
  machine_requested_at: string | null;
  /** The command's exit code for a one-task run; `null` beyond that. */
  exit_code: number | null;
  error: string | null;
  /**
   * Whether at least one task of this run has a log that can be read **now**.
   *
   * A container the console still holds, or a tail it kept when a task failed.
   * It is not a claim that every task's log is there, and it stops being true
   * when the last of them goes.
   */
  has_logs: boolean;
  mounts: Mount[];
  events: CpuRunEvent[];
}

/**
 * `GET /api/v1/cpu-runs/{id}` query.
 *
 * `events_after` narrows the trace and nothing else: the counters, the state
 * and the deadline are always the current ones.
 */
export interface CpuRunQuery {
  events_after?: number;
}

/**
 * `POST /api/v1/cpu-runs` body.
 *
 * The three fan-out fields are always sent, at the defaults `docs/API.md`
 * gives them when the form has nothing else to say — `tasks: 1`, `retries: 0`,
 * `max_parallel_tasks: null`. A run of one command is that body, not a shorter
 * one: leaving fields out to let the server fill them in would make the
 * request depend on which defaults that deployment happens to hold.
 */
export interface CpuRunCreate {
  name: string;
  image: string;
  command: string;
  /** Cores **one task** needs — never the run's total. */
  cores: number;
  hours: number;
  /** How many times the command runs, 1 to `defaults.max_tasks_per_run`. */
  tasks: number;
  /**
   * Extra attempts for a failed task, `0` unless the submitter asks.
   *
   * A command that may have had side effects is never silently re-run, so this
   * is the researcher's statement that their tasks are idempotent — the
   * console never decides it for them.
   */
  retries: number;
  /**
   * `null` = as many at once as the fleet will give.
   *
   * A throttle for the submitter's own sake — a task hitting a rate-limited
   * API, a shared database — never a rationing mechanism.
   */
  max_parallel_tasks: number | null;
}

export interface CpuRunListQuery {
  mine?: boolean;
  state?: CpuRunState;
  limit?: number;
}

export interface CpuRunList {
  items: CpuRun[];
}

/**
 * Where one task of a run stands.
 *
 * The same column the run's five counters aggregate, so a page and the
 * counters can never disagree about what a task is. `not_run` is a task that
 * never received a container before the run ended; **no screen may present it
 * as a failure**.
 */
export type CpuTaskState =
  | "waiting"
  | "running"
  | "succeeded"
  | "failed"
  | "not_run";

/**
 * What a request for this task's log would find, without asking for it.
 *
 * - `kept`: there is something to read now — a container the console still
 *   holds, or the tail it kept when the task failed.
 * - `gone`: the machine that wrote it has been given back and nothing was kept.
 * - `pending`: nothing has been written yet.
 */
export type CpuTaskLogState = "kept" | "gone" | "pending";

/** One task of a run, as the console's own record holds it. */
export interface CpuRunTask {
  index: number;
  state: CpuTaskState;
  /** Counts from 0 and says which try this row describes. */
  attempt: number;
  /** The console's own name for the machine; `null` until the task has one. */
  machine: string | null;
  started_at: string | null;
  finished_at: string | null;
  /** The exit code of the attempt this row describes, when there is one. */
  exit_code: number | null;
  log: CpuTaskLogState;
}

/**
 * `GET /api/v1/cpu-runs/{id}/tasks` — a page of tasks, lowest index first.
 *
 * **A page is a snapshot.** The rows move while they are being read, so across
 * one walk a task may appear twice or not at all; `index` is a task's identity
 * and `attempt` says which try a row describes. Anything that has to be exact —
 * how many failed — is the counter on the run.
 */
export interface CpuRunTaskList {
  items: CpuRunTask[];
  /** Rows matching **this filter**, never the run's task count. */
  total: number;
  /** The index to pass next; `null` on the last page. */
  next_after: number | null;
}

/** `GET /api/v1/cpu-runs/{id}/tasks` query — keyset on the index, never an offset. */
export interface CpuRunTaskQuery {
  state?: CpuTaskState;
  /** Exclusive: the page starts at the first index above this one. */
  after?: number;
  /** 100 by default, 500 at most. */
  limit?: number;
}

/**
 * `POST /api/v1/cpu-runs/preview` → what submitting this body would do.
 *
 * The same body as a creation, the same guards in the same order, and nothing
 * written. **`lines` are the console's own sentences and a client renders them
 * verbatim** — in order, unparaphrased, unabridged, and never beside
 * arithmetic of its own. The structured fields exist so a client can lay out a
 * table, not so it can recompute the prose.
 */
export interface CpuRunPreview {
  lines: string[];
  /** `tasks × cores`: arithmetic about the request, not a reservation. */
  cores_total: number;
  /**
   * How many machines those tasks pack into — a packing figure that reserves
   * nothing, and `null` when the console cannot honestly say. A null is never
   * divided into anything.
   */
  machines_needed: number | null;
  /** The shared folder this run would get; `null` on a console with none. */
  work_gb: number | null;
  run_hours: number;
  /** The declared price of **one machine** of the shape, per hour. */
  eur_per_hour: number | null;
}

// ----------------------------------------------------------- CPU workspaces

/**
 * An interactive CPU workspace: the workspace promise, in cores.
 *
 * A fourth workload kind (`cpu_workspace`), never a workspace with `gpus: 0`.
 * It walks the **workspace** states with no new members, carries the same
 * connection block, the same deadline and the same trace — and two groups of
 * fields are **absent rather than null**, deliberately:
 *
 * - `gpus` and `gpu_indices`: there are none, and an empty `gpu_indices`
 *   already means "the console could not measure which devices this got".
 * - `utilization_pct`, `idle_since`, `idle_stops_at`: allocation comes from
 *   Kubernetes and is exact, but **activity has no source on a CPU machine**.
 *   Nothing there separates a computing process from a waiting one, so there
 *   is no idle countdown and there never will be. A field that existed and was
 *   always null would invite a client to wait for the day it is filled in.
 *
 * There is also no resize: `POST /cpu-workspaces/{id}/resize` does not exist
 * and answers 404. To change the size, stop it and start another one.
 */
export interface CpuWorkspace {
  id: string;
  user: string;
  /** The workspace walk, with no new members for this kind. */
  state: WorkspaceState;
  /** Whole cores reserved for it — nobody can take them away. */
  cores: number;
  /**
   * GiB of memory it holds, per core, reserved **and** limited exactly as a GPU
   * workspace's is. The reservation is what protects it on a shared machine;
   * going past the limit stops everything running inside the workspace at once,
   * and the workspace starts again on the same machine.
   */
  memory_gb: number;
  image: string;
  /**
   * The machine it is on, and it is one machine.
   *
   * Other people's tasks and workspaces are on it too: what protects this one
   * is the reservation, not the machine.
   */
  node: string | null;
  created_at: string;
  started_at: string | null;
  /** `null` while waiting for a machine: the clock starts when it is usable. */
  expires_at: string | null;
  warning_at: string | null;
  /** Counted place in the queue; `null` unless waiting for a machine. */
  queue_position: number | null;
  /**
   * When a machine was really requested **for this workspace** — the narrow
   * twin of `waiting.machine`, which is what says whether one is coming for
   * its turn. A machine being started for the request ahead ends this wait
   * too, and this field knows nothing about it.
   */
  machine_requested_at: string | null;
  /** The whole of the wait; `null` unless `state` is `waiting_for_node`. */
  waiting: Waiting | null;
  /** `null` until it is usable **and** its port is published to the proxy. */
  connection: WorkspaceConnection | null;
  mounts: Mount[];
  /**
   * Whether there is a log to read **right now**.
   *
   * True from the moment the workspace starts printing, false again once
   * its machine has been given back — so a screen never offers a log and
   * then answers that there is nothing left to read.
   */
  has_logs: boolean;
  /** Why it failed, in plain English. `null` unless `state` is `failed`. */
  error: string | null;
  events: WorkloadEvent[];
}

/**
 * `POST /api/v1/cpu-workspaces` body.
 *
 * `hours` is the **workspace** pair — `workspace_hours` / `max_workspace_hours`
 * in the top-level `defaults` — not a pair of this kind's own.
 */
export interface CpuWorkspaceCreate {
  /** One of `capacity.cpu.defaults.allowed_workspace_cores`. */
  cores: number;
  hours: number;
  image: string;
}

export interface CpuWorkspaceListQuery {
  mine?: boolean;
  state?: WorkspaceState;
  limit?: number;
}

export interface CpuWorkspaceList {
  items: CpuWorkspace[];
}

/**
 * `POST /api/v1/nodes` request body — start machines of one shape.
 *
 * `gpus_per_node` picks the pool (a 4-GPU or an 8-GPU machine); `count` is how
 * many, 1 or 2 at a time.
 */
export interface NodeRequest {
  gpus_per_node: number;
  count: number;
}

/** `POST /api/v1/nodes` → 202. `pool` is the one that grew. */
export interface NodeRequestResult {
  pool: PoolInfo;
  eta_minutes: number;
  message: string;
}

// --------------------------------------------------------------- workspaces

export type WorkspaceState =
  | "waiting_for_node"
  | "starting"
  | "ready"
  | "expiring"
  | "stopped"
  | "expired"
  | "failed";

/** Workspace states after which nothing moves any more. */
export const TERMINAL_WORKSPACE_STATES: readonly WorkspaceState[] = [
  "stopped",
  "expired",
  "failed",
];

/** States where the workspace is not usable yet. */
export const PENDING_WORKSPACE_STATES: readonly WorkspaceState[] = [
  "waiting_for_node",
  "starting",
];

export interface WorkspaceConnection {
  ssh_command: string;
  /**
   * The same command with the LoadBalancer's address instead of its name, read
   * from the cluster. `null` when the address could not be read — never
   * guessed.
   */
  ssh_host_ip: string | null;
  ssh_command_ip: string | null;
  ssh_config: string;
  vscode_url: string;
  ready: boolean;
}

export interface Mount {
  path: string;
  description: string;
  persistent: boolean;
}

/** One line of the plain-English trace the workspace page renders verbatim. */
export interface WorkloadEvent {
  at: string;
  kind: string;
  message: string;
}

export interface Workspace {
  id: string;
  user: string;
  state: WorkspaceState;
  gpus: number;
  /** Cores reserved for it — what it holds, not what it is using. */
  cores: number;
  /**
   * GiB of memory reserved **and** limited: a program that goes past this is
   * stopped, everything else in the workspace stops with it, and the workspace
   * starts again on the same machine.
   *
   * It is the figure this workspace's own machine holds, read back from the
   * cluster — never `defaults.per_gpu.memory_gb × gpus`, which is only the
   * answer for a workspace that has no machine yet.
   */
  memory_gb: number;
  /** Which GPU the ask meant (ADR-0020); `null` means the deployment default. */
  gpu_family: string | null;
  image: string;
  node: string | null;
  gpu_indices: number[];
  created_at: string;
  started_at: string | null;
  /** `null` while waiting for a machine: the clock starts when usable. */
  expires_at: string | null;
  warning_at: string | null;
  /** Counted place in the queue; `null` unless waiting for a machine. */
  queue_position: number | null;
  /**
   * When a machine was really requested **for this workspace**.
   *
   * The narrow twin of `waiting.machine`: `null` means no machine was asked
   * for by this request, which is **not** the same as no machine coming — one
   * being started for the request ahead of it ends this wait too. What says
   * whether a machine is on its way for this turn is `waiting.machine`, and
   * reading this field as the answer is what told a researcher that nothing
   * was starting while a machine was booting in front of her.
   */
  machine_requested_at: string | null;
  /** The whole of the wait; `null` unless `state` is `waiting_for_node`. */
  waiting: Waiting | null;
  /** First moment the GPUs were measured doing nothing. */
  idle_since: string | null;
  /** When an unused workspace will be stopped, so the countdown can be shown. */
  idle_stops_at: string | null;
  /** `null` until the workspace is ready **and** its port is published. */
  connection: WorkspaceConnection | null;
  mounts: Mount[];
  /** `null` without telemetry, or when the samples could not be attributed. */
  utilization_pct: number | null;
  /**
   * Whether there is a log to read **right now**.
   *
   * True from the moment the workspace starts printing, false again once
   * its machine has been given back — so a screen never offers a log and
   * then answers that there is nothing left to read.
   */
  has_logs: boolean;
  /** Why it failed, in plain English. `null` unless `state` is `failed`. */
  error: string | null;
  events: WorkloadEvent[];
}

export interface WorkspaceCreate {
  gpus: number;
  hours: number;
  image: string;
  /** Which GPU (ADR-0020). Omitted means the deployment's default family. */
  gpu_family?: string;
}

export interface WorkspaceListQuery {
  mine?: boolean;
  state?: WorkspaceState;
  limit?: number;
}

export interface WorkspaceList {
  items: Workspace[];
}

/**
 * `POST /api/v1/workspaces/{id}/extend` body — keep the workspace longer.
 *
 * A workspace's total lifetime is not capped: `hours` alone is bounded, by
 * `MAX_WORKSPACE_HOURS` per call, and the refusal says to extend again.
 */
export interface ExtendRequest {
  hours: number;
}

/**
 * `POST /api/v1/workspaces/{id}/shorten` body — give the GPUs back early.
 *
 * `minutes` counts from now, not from the current deadline: it is how much
 * longer the workspace is needed. Everything running inside keeps running
 * until then. The server refuses anything under fifteen minutes and says so.
 */
export interface ShortenRequest {
  minutes: number;
}

/**
 * `POST /api/v1/workspaces/{id}/resize` body — take more GPUs or give some
 * back.
 *
 * The environment restarts on the new GPU count; everything in the home
 * folder is kept, anything in `/scratch` and any running process is not.
 */
export interface ResizeRequest {
  gpus: number;
}

// ------------------------------------------------------------- environments

/**
 * One environment a workspace or job can run — built-in, or saved by someone
 * on the team.
 *
 * `id` is exactly what the `image` field of a workspace or job accepts: a
 * built-in catalogue key, or a saved environment's id.
 */
export interface EnvironmentOption {
  id: string;
  name: string;
  description: string | null;
  /** The container image actually pulled. */
  reference: string;
  /** Where it comes from: the deployment's catalogue, or somebody's save. */
  source: "built-in" | "saved";
  /** Who saved it; `null` for built-in entries. */
  added_by: string | null;
  /** Whether the whole team sees it. Built-in entries always do. */
  shared: boolean;
  /** Whether the caller may edit or delete it — their own saves only. */
  editable: boolean;
}

export interface EnvironmentList {
  items: EnvironmentOption[];
}

/** `POST /api/v1/environments` body. */
export interface EnvironmentCreate {
  name: string;
  description?: string;
  reference: string;
  shared: boolean;
}

/** `PATCH /api/v1/environments/{id}` body — only what changes. */
export interface EnvironmentUpdate {
  name?: string;
  description?: string;
  reference?: string;
  shared?: boolean;
}

// --------------------------------------------------------------------- jobs

export type JobState =
  | "waiting_for_node"
  | "starting"
  | "running"
  | "succeeded"
  | "failed"
  | "cancelled"
  | "expired";

/** Job states after which nothing moves any more. */
export const TERMINAL_JOB_STATES: readonly JobState[] = [
  "succeeded",
  "failed",
  "cancelled",
  "expired",
];

/**
 * A batch job.
 *
 * `docs/API.md` specifies the creation payload and the state machine; the
 * remaining fields are the shared workload fields it lists for workspaces.
 */
export interface Job {
  id: string;
  user: string;
  name: string;
  state: JobState;
  gpus: number;
  /** Which GPU the ask meant (ADR-0020); `null` means the deployment default. */
  gpu_family: string | null;
  image: string;
  command: string;
  node: string | null;
  gpu_indices: number[];
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  expires_at: string | null;
  /** Counted place in the queue; `null` unless waiting for a machine. */
  queue_position: number | null;
  /**
   * When a machine was really requested **for this job** — the narrow twin of
   * `waiting.machine`, which is what says whether one is coming for its turn.
   */
  machine_requested_at: string | null;
  /** The whole of the wait; `null` unless `state` is `waiting_for_node`. */
  waiting: Waiting | null;
  exit_code: number | null;
  error: string | null;
  /** `null` without telemetry, or when the samples could not be attributed. */
  utilization_pct: number | null;
  /** Whether a container ever started, i.e. whether there is a log to read. */
  has_logs: boolean;
  events: WorkloadEvent[];
}

export interface JobCreate {
  name: string;
  image: string;
  command: string;
  gpus: number;
  hours: number;
  /** Which GPU (ADR-0020). Omitted means the deployment's default family. */
  gpu_family?: string;
}

export interface JobListQuery {
  mine?: boolean;
  state?: JobState;
  limit?: number;
}

export interface JobList {
  items: Job[];
}

// -------------------------------------------------------- training elsewhere

/**
 * The one capability that exists (`docs/AUTH.md` §2, ADR-0011).
 *
 * Spelled with an underscore wherever a client matches on it — `/me` publishes
 * it this way — while the URL that records it spells the section `jean-zay`.
 *
 * It **records** that the team has declared this account to IDRIS. It admits
 * nobody to anything: no route is behind it, and it decides one thing only —
 * whether `Relay.jean_zay` is filled in or `null`.
 */
export const JEAN_ZAY_CAPABILITY = "jean_zay";

/**
 * The tunnel that puts the team's MLflow on a run's own `localhost`.
 *
 * `null` on `Relay` when `MLFLOW_PRIVATE_URL` is unset: the block is honestly
 * absent rather than pointing at an address nobody chose.
 */
export interface RelayMlflow {
  /** Fixed at 5000, because the `export` line everybody pastes says so. */
  local_port: number;
  /** The whole `ssh -f -N -o ExitOnForwardFailure=yes -L …` line, server-built. */
  tunnel_command: string;
  /** `http://localhost:5000` — what the run will talk to. */
  tracking_uri: string;
  /** The `export MLFLOW_TRACKING_URI=…` line, the second half of the paste. */
  export_command: string;
}

/**
 * What the team has recorded about this account at IDRIS.
 *
 * `null` on `Relay` for an account nobody has declared. It holds nothing about
 * Jean Zay itself: the console has no address there, does not know your login
 * there and could not use it.
 */
export interface RelayJeanZay {
  granted_at: string;
  granted_by: string;
}

/**
 * `GET /api/v1/relay` — *Training elsewhere*, for every approved researcher.
 *
 * One machine of Neuralk's, outside the cluster, on both sides of the private
 * network: a tunnel through it reaches the team's MLflow from wherever a run
 * happens, and IDRIS accepts our Jean Zay connections from it. Reaching it
 * comes with being approved, exactly like the GPUs — there is no second
 * permission to hold, and no route here refuses on one.
 *
 * Everything is configuration the server was given, never a guess: while the
 * machine is still being created the endpoint answers `503 not_connected` and
 * no address is invented.
 */
export interface Relay {
  /** `RELAY_SSH_HOST`, the machine that is on both sides of the line. */
  host: string;
  port: number;
  /** The console username: the same account name on the other side. */
  username: string;
  /** Carries `-p` only when the port is not 22. */
  ssh_command: string;
  /** The `~/.ssh/config` stanza; its alias is fixed — one machine, one address. */
  ssh_config: string;
  /** Whether the caller has a public key in their profile. */
  key_on_file: boolean;
  /**
   * What the screen renders as "you can connect".
   *
   * A `200` already means the address is configured, so this is false in
   * exactly one case: no key. It is **not** proof the machine already has that
   * key — it fetches the list about once a minute and the console does not
   * watch it do so.
   */
  ready: boolean;
  /** The everyday half; `null` when the deployment has no MLflow address. */
  mlflow: RelayMlflow | null;
  /** The second half; `null` for an account the team has not declared. */
  jean_zay: RelayJeanZay | null;
}

/**
 * One row of `user_entitlements`, as the two admin routes answer.
 *
 * The record holds exactly while `revoked_at` is `null`: withdrawing stamps
 * the row rather than deleting it, so an account the team once declared to
 * IDRIS still says so.
 */
export interface Entitlement {
  username: string;
  capability: string;
  granted_at: string;
  granted_by: string;
  revoked_at: string | null;
}

// ----------------------------------------------------------------------- me

/**
 * Where the console may install a key — and nothing else.
 *
 * There are two because the console writes exactly two `authorized_keys`
 * files: the one in a researcher's own workspaces, and the one on the single
 * machine it keeps off the cluster (`docs/AUTH.md` §4). A scope never says
 * what a key may do once it is installed.
 */
export type KeyScope = "workspaces" | "elsewhere";

/** One registered public key: the line, where it may go, what it is called. */
export interface RegisteredKey {
  /** The public half, one line, as `ssh-keygen -y` prints it. */
  public_key: string;
  /**
   * Where the console may install it — never empty, and the server refuses an
   * empty one: a key installable nowhere is a row nobody can explain.
   */
  scopes: KeyScope[];
  /**
   * What the researcher calls that machine, or `null`.
   *
   * For when the key's own comment names a person rather than a machine. The
   * console never writes one.
   */
  label: string | null;
}

export interface Me {
  username: string;
  email: string | null;
  state: AccountState;
  /** Whether this account may approve other people and stop their work. */
  is_admin: boolean;
  /**
   * What an admin has recorded about this account (`docs/AUTH.md` §2).
   *
   * Today `"jean_zay"` is the only value that exists, and it means *the team
   * has declared this person to IDRIS*. It opens no screen and closes none —
   * *Training elsewhere* belongs to every approved researcher — it decides
   * only what that screen's payload carries.
   *
   * **An admin is not entitled by being an admin.** The two fields are
   * independent, and inferring one from the other is a bug.
   */
  entitlements: string[];
  /**
   * Every public key on file, one per machine, newest last — the order given.
   *
   * Empty until the researcher registers one. Each key carries the places the
   * console may install it, so a laptop key and the key that sits on a shared
   * cluster login node no longer open the same doors.
   */
  ssh_public_keys: RegisteredKey[];
  has_home_volume: boolean;
  /** The shell the next workspace hands them. Never the running one. */
  shell: Shell;
  /**
   * Emergency stop: no new workspace and no new machine, cluster-wide.
   *
   * The only gate the account screen still carries. There are no per-person
   * quotas: the product makes GPUs easy to take and easy to give back, it
   * does not ration them.
   */
  console_paused: boolean;
  /**
   * Whether this console can send warnings as Slack direct messages.
   *
   * It says what the **deployment** can do — a Slack app is configured — and
   * deliberately not whether Slack knows *this* person's address: that is
   * only learned the first time a message is sent, and the workload's own
   * trace says so in a sentence. A field that guessed would be the one place
   * the product lied about a warning being delivered.
   *
   * False is the honest default for a bundle talking to a console that
   * publishes nothing: the copy then keeps today's promise, that the console
   * page is where a warning appears.
   */
  slack_notifications: boolean;
  /** Why access was turned off, when the server records one. */
  disabled_reason?: string | null;
}

/** What a workspace drops you into over SSH. */
export type Shell = "zsh" | "bash";

export interface ShellUpdate {
  shell: Shell;
}

export interface MeUpdate {
  /**
   * The whole list, which is what makes removal possible.
   *
   * `PUT` replaces: sending three keys leaves exactly those three, removing
   * one means sending the others, and narrowing one means sending it again
   * with fewer scopes. The field is required — a body without it is refused
   * rather than read as "erase every key" — and `[]` is the explicit way to
   * have none (`docs/API.md`).
   */
  ssh_public_keys: RegisteredKey[];
}

// ------------------------------------------------------------- console keys

/**
 * One console key, as `GET /api/v1/me/keys` lists it.
 *
 * There is no `key` field here, and there cannot be one: the console keeps a
 * SHA-256 hash and the readable prefix, so it can say *which* keys exist and
 * can never produce one (`docs/AUTH.md` §7).
 */
export interface ConsoleKey {
  id: string;
  /** The label its owner chose — the machine it lives on. Not unique. */
  name: string;
  /**
   * The eight readable characters that tell one key from another.
   *
   * Not a truncated secret and it cannot be turned into one, which is why it
   * may be shown here at all.
   */
  prefix: string;
  created_at: string;
  /**
   * When it last authenticated a request; `null` until it has authenticated
   * one.
   *
   * Written at a resolution of one minute, so it is accurate to the minute and
   * never to the second: no interface may present it as a live indicator.
   */
  last_used_at: string | null;
  /** When it stops working; `null` for a key with no expiry — the default. */
  expires_at: string | null;
}

/**
 * `POST /api/v1/me/keys` → `201`: the only payload that ever carries a secret.
 *
 * `key` appears in this response and in no other, ever. It is shown once, is
 * never stored by this application, and a researcher who loses it revokes the
 * key and creates another.
 */
export interface CreatedConsoleKey extends ConsoleKey {
  /** The whole key, `nk_<prefix>_<secret>`. Shown once, then forgotten. */
  key: string;
}

export interface ConsoleKeyList {
  items: ConsoleKey[];
}

/**
 * How long a new key lives: `null` is the default and means no expiry.
 *
 * The server accepts exactly these three values; anything else is a
 * `400 validation_error` (`docs/API.md`).
 */
export type ConsoleKeyExpiry = null | 30 | 90;

/** Body of `POST /api/v1/me/keys`. */
export interface ConsoleKeyCreate {
  /** 1–64 characters, the label its owner chose. */
  name: string;
  expires_days: ConsoleKeyExpiry;
}

// -------------------------------------------------------------------- admin

/** One account as `GET /api/v1/admin/users` lists it. */
export interface AdminUser {
  username: string;
  email: string | null;
  state: AccountState;
  is_admin: boolean;
  /** What has been recorded about it — the screen that marks shows the marks. */
  entitlements: string[];
  /** First Google sign-in; `null` when the server did not record one. */
  first_seen_at: string | null;
  /** GPU-hours spent over the rolling week. */
  weekly_gpu_hours_used: number;
  /** Their live workspace, `null` when they have none. */
  workspace_id: string | null;
  /** Why access was turned off; `null` unless the account is disabled. */
  disabled_reason: string | null;
}

export interface AdminUserList {
  items: AdminUser[];
}

/**
 * One person's consumption over the window of `GET /api/v1/admin/usage`.
 *
 * Information, never a quota (ADR-0008): nothing refuses work from it.
 */
export interface AccountUsage {
  /** Empty on the totals row. */
  username: string;
  email: string | null;
  /** GPUs held × hours held inside the window, from the placement ledger. */
  gpu_hours: number;
  /**
   * Of which measured computing (DCGM). Counted per workload, not per window;
   * `null` when none of the account's workloads was ever sampled — never `0`
   * for that.
   */
  busy_gpu_hours: number | null;
  /** Cores × machines × hours inside the window, for CPU work. */
  cpu_core_hours: number;
  /** Distinct interactive workloads, of either fleet, that held something. */
  workspaces: number;
  /** Distinct jobs and CPU runs that held something. */
  runs: number;
}

/** Payload of `GET /api/v1/admin/usage`, busiest first. */
export interface UsageReport {
  days: number;
  since: string;
  until: string;
  users: AccountUsage[];
  totals: AccountUsage;
}

/** The declared rates your figures were priced with — null when none is. */
export interface UsagePrices {
  /**
   * Euros per GPU-hour, when every GPU-hour of the window is of one family
   * with a declared price; null when no price is declared or when several
   * families were held (the cost fields are still exact then).
   */
  gpu_hour_eur: number | null;
  /** Always null today: no core-hour rate is derived. */
  cpu_core_hour_eur: number | null;
}

/** What the window cost — each figure null when it cannot be priced. */
export interface UsageCost {
  gpu_eur: number | null;
  /** The part of `gpu_eur` for GPU-hours held but not measured computing. */
  idle_gpu_eur: number | null;
  cpu_eur: number | null;
  total_eur: number | null;
}

/**
 * Payload of `GET /api/v1/me/usage`: your own consumption and its price.
 *
 * Information for the person reading it, never a quota (ADR-0008, ADR-0025):
 * nothing anywhere refuses work because of a number here.
 */
export interface MyUsage {
  days: number;
  since: string;
  until: string;
  /** GPUs held × hours held inside the window. */
  gpu_hours: number;
  /** Of which measured computing; null when never sampled — never 0 for that. */
  busy_gpu_hours: number | null;
  /** `gpu_hours` minus `busy_gpu_hours`; null whenever busy is. */
  idle_gpu_hours: number | null;
  cpu_core_hours: number;
  workspaces: number;
  runs: number;
  /** The one GPU family the GPU-hours belong to; null when none or several. */
  family: string | null;
  prices: UsagePrices;
  cost: UsageCost;
}

/** `POST /api/v1/admin/users/{username}/disable` body. */
export interface DisableRequest {
  reason: string;
}

/** `POST /api/v1/admin/pause` body. */
export interface PauseRequest {
  paused: boolean;
  reason: string;
}

/** What the pause switch answers; the truth also reaches the UI via `me`. */
export interface PauseState {
  paused: boolean;
  reason: string | null;
}

/** One line of `GET /api/v1/admin/audit`: who did what to whom, and why. */
export interface AuditEntry {
  id: string;
  at: string;
  actor: string;
  action: string;
  subject: string | null;
  reason: string | null;
}

/** What an admin is looking for in the log; every field optional, ANDed. */
export interface AuditQuery {
  /** Rows per page, 1–200. */
  limit?: number;
  /** Rows to skip; past the end is an empty page with the true total. */
  offset?: number;
  /** Case-insensitive substring over actor, action, subject and reason. */
  q?: string;
  /** Exact username, or `console` for the loop's own acts. */
  actor?: string;
  /** Exact action value. */
  action?: string;
  /** ISO-8601, inclusive lower bound on `at`. */
  since?: string;
  /** ISO-8601, exclusive upper bound on `at`. */
  until?: string;
}

/** One page of `GET /api/v1/admin/audit`, newest first, with its total. */
export interface AuditList {
  items: AuditEntry[];
  /** Every row the filters match, page or no page — "1–50 of 1,234". */
  total: number;
  limit: number;
  offset: number;
}
